import { useEffect, useState } from 'react';
import { animate, motion, useMotionValue } from 'framer-motion';
import useMeasure from 'react-use-measure';
import { cn } from '../../utils';

const InfiniteSlider = ({
  children,
  gap = 16,
  duration = 25,
  durationOnHover,
  direction = 'horizontal',
  reverse = false,
  className,
}) => {
  const [currentDuration, setCurrentDuration] = useState(duration);
  const [ref, { width, height }] = useMeasure();
  const translation = useMotionValue(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [key, setKey] = useState(0);

  useEffect(() => {
    let controls;
    const size = direction === 'horizontal' ? width : height;
    const contentSize = size + gap;
    const from = reverse ? -contentSize / 2 : 0;
    const to = reverse ? 0 : -contentSize / 2;

    if (!size) return undefined;

    if (isTransitioning) {
      controls = animate(translation, [translation.get(), to], {
        ease: 'linear',
        duration: currentDuration * Math.abs((translation.get() - to) / contentSize),
        onComplete: () => {
          setIsTransitioning(false);
          setKey((previousKey) => previousKey + 1);
        },
      });
    } else {
      controls = animate(translation, [from, to], {
        ease: 'linear',
        duration: currentDuration,
        repeat: Infinity,
        repeatType: 'loop',
        repeatDelay: 0,
        onRepeat: () => translation.set(from),
      });
    }

    return () => controls?.stop();
  }, [key, translation, currentDuration, width, height, gap, isTransitioning, direction, reverse]);

  const hoverProps = durationOnHover ? {
    onHoverStart: () => {
      setIsTransitioning(true);
      setCurrentDuration(durationOnHover);
    },
    onHoverEnd: () => {
      setIsTransitioning(true);
      setCurrentDuration(duration);
    },
  } : {};

  const trackStyle = {
    gap: `${gap}px`,
    flexDirection: direction === 'horizontal' ? 'row' : 'column',
  };

  return (
    <div className={cn('overflow-hidden', className)}>
      <motion.div
        className="flex w-max"
        style={{ ...(direction === 'horizontal' ? { x: translation } : { y: translation }), ...trackStyle }}
        ref={ref}
        {...hoverProps}
      >
        <div className="flex w-max items-center" style={trackStyle}>{children}</div>
        <div className="flex w-max items-center" style={trackStyle} aria-hidden="true">{children}</div>
      </motion.div>
    </div>
  );
};

export { InfiniteSlider };